import dayjs, { Dayjs, isDayjs, ManipulateType, UnitType } from 'dayjs';
import {
  isDate,
  isNonNaNInteger,
  isString,
  isWholeNumber,
} from '@/utils/CommonUtils';
const customParseFormat = require('dayjs/plugin/customParseFormat');

dayjs.extend(customParseFormat);
export class PortalDate {
  private value: Dayjs;
  private formatStr: string;

  protected constructor(value: Dayjs, formatStr: string) {
    this.formatStr = formatStr;
    this.value = value;
  }

  public toDayjs(): Dayjs {
    return this.value;
  }

  public toDate() {
    return this.value.toDate();
  }

  public toString() {
    return this.value.format(this.formatStr);
  }

  public toEpochSeconds() {
    return this.value.unix();
  }

  public isValid() {
    return this.value.isValid();
  }

  public add(value: number, unit: ManipulateType): PortalDate {
    if (!isNonNaNInteger(value)) {
      throw Error('The passed value is not an integer');
    }
    return newPortalDate(this.value.add(value, unit), this.formatStr);
  }

  public subtract(value: number, unit: ManipulateType): PortalDate {
    if (!isNonNaNInteger(value)) {
      throw Error('The passed value is not an integer');
    }
    return newPortalDate(this.value.subtract(value, unit), this.formatStr);
  }

  public get(unit: UnitType): number {
    return this.value.get(unit);
  }

  public isSameDay(anotherDate: string): boolean {
    const targetDate = newPortalDate(this.value, 'YYYY-MM-DD').toString();
    return targetDate === anotherDate;
  }
}

// Do not use new PortalDayjsDate(), please use newPortalDate()
export class PortalDayjsDate extends PortalDate {
  constructor(value: Dayjs, formatStr = '') {
    super(value, formatStr);
  }
}

// Do not use new PortalDateDate(), please use newPortalDate()
export class PortalDateDate extends PortalDate {
  constructor(value: Date, formatStr = '') {
    super(dayjs(value), formatStr);
  }
}

// Do not use new PortalNumberDate(), please use newPortalDate()
export class PortalNumberDate extends PortalDate {
  constructor(value: number, formatStr = '') {
    super(dayjs.unix(value), formatStr);
  }
}

// Do not use new PortalStringDate(), please use newPortalDate()
export class PortalStringDate extends PortalDate {
  constructor(value: string, formatStr = '') {
    const useStrictParsing = true;
    super(dayjs(value, formatStr, useStrictParsing), formatStr);
  }
}

export const newPortalDate = (
  value: Dayjs | Date | string | number,
  formatStr: string = ''
): PortalDate => {
  if (isDayjs(value)) {
    return new PortalDayjsDate(value, formatStr);
  } else if (isDate(value)) {
    return new PortalDateDate(value as Date, formatStr);
  } else if (isString(value)) {
    return new PortalStringDate(value as string, formatStr);
  } else if (isWholeNumber(value as number)) {
    return new PortalNumberDate(value as number, formatStr);
  } else {
    return new PortalDayjsDate(dayjs('Invalid Date'), formatStr);
  }
};
